/**
 * Bedrijfskeuze (US-003, design-handoff scherm 2).
 *
 * Dit scherm verschijnt alleen bij rechten voor meer dan één bedrijf; de server stuurt bij
 * precies één bedrijf rechtstreeks door naar het prijsoverzicht.
 */

import { ROLE_LABELS } from '@prijsmonitor/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { Beeldmerk } from '../components/Beeldmerk.js';
import { useSession } from '../session/SessionContext.js';
import styles from './AuthScreens.module.css';

/** Bedrijfsafkorting voor het logoblok, bv. "NPI" of "SC". */
function abbreviate(name: string): string {
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length === 1) return (words[0] as string).slice(0, 3).toUpperCase();
  return words
    .slice(0, 3)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

export function CompanyPickerScreen(): React.JSX.Element {
  const { session, chooseCompany } = useSession();
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function open(companyId: string): Promise<void> {
    setBusyId(companyId);
    setError(null);
    try {
      await chooseCompany(companyId);
      void navigate('/prijsoverzicht');
    } catch {
      setError('Het bedrijf kon niet worden geopend. Probeer het opnieuw.');
      setBusyId(null);
    }
  }

  return (
    <div className={styles.gradientPage}>
      <div className={styles.pickerCard}>
        <div className={styles.header}>
          <Beeldmerk width={36} alt="" />
          <div>
            <div className={styles.titleSmall}>Kies een bedrijf</div>
            <div className={styles.subtitle}>
              Je bent geautoriseerd voor de onderstaande bedrijven
            </div>
          </div>
        </div>

        <div className={styles.dividerTight} />

        {error && (
          <div className={styles.error} role="alert">
            <span aria-hidden="true">⚠</span>
            <span>{error}</span>
          </div>
        )}

        <div className={styles.companyList}>
          {session?.memberships.map((membership) => (
            <button
              key={membership.companyId}
              type="button"
              className={styles.companyRow}
              onClick={() => void open(membership.companyId)}
              disabled={busyId !== null}
            >
              {membership.heeftLogo ? (
                <img
                  className={styles.companyLogoAfbeelding}
                  src={`/api/bedrijven/${membership.companyId}/logo`}
                  alt=""
                />
              ) : (
                <span className={styles.companyLogo} aria-hidden="true">
                  {abbreviate(membership.companyName)}
                </span>
              )}
              <span className={styles.companyText}>
                <span className={styles.companyName}>{membership.companyName}</span>
              </span>
              <span className={styles.roleChip}>{ROLE_LABELS[membership.role]}</span>
              <span className={styles.arrow} aria-hidden="true">
                {busyId === membership.companyId ? <span className="spinner" /> : '→'}
              </span>
            </button>
          ))}
        </div>

        <p className={styles.note}>
          Heb je rechten voor één bedrijf, dan wordt dit scherm overgeslagen en opent dat bedrijf
          direct. Na het wisselen van bedrijf zie je uitsluitend gegevens van het gekozen bedrijf.
        </p>
      </div>
    </div>
  );
}
